import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlaceMapView } from '@/features/place/PlaceMapView';
import { guessCategory, type PlaceResult } from '@/features/place/api';
import { RouteBadge } from '@/features/route/RouteBadge';
import type { RouteCoords } from '@/features/route/useRoute';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';
import { Dialog } from '@/shared/ui/Dialog';
import { useToast } from '@/shared/ui/Toast';
import { createPlaceCandidate, listPlaceCandidates, type PlaceCandidate } from '@/shared/api/voting';
import { getMeeting, updateMeetingPoint } from '@/shared/api/meetings';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { createScheduleItem, deleteScheduleItem, listScheduleItems, updateScheduleItem, type ScheduleItem } from '@/shared/api/schedule';
import { fmtKst } from '@/shared/lib/date';

// 모임 컨텍스트에서 지도 기반 장소 검색.
// 기본: 장소 후보 등록 / ?for=meeting-point: 만나는 장소(집결지) 지정.
function candidateToPlaceResult(candidate: PlaceCandidate): PlaceResult {
  return {
    id: candidate.id,
    name: candidate.name,
    category: candidate.category,
    address: candidate.address ?? '',
    roadAddress: candidate.address ?? '',
    phone: '',
    x: Number(candidate.x),
    y: Number(candidate.y),
    placeUrl: candidate.url ?? '',
    rating: null,
    userRatingCount: null,
  };
}


function sameCandidate(place: PlaceCandidate, result: PlaceResult) {
  const sameUrl = place.url && result.placeUrl && place.url.trim() === result.placeUrl.trim();
  const sameCoords =
    place.x &&
    place.y &&
    Math.abs(Number(place.x) - Number(result.x)) < 0.000001 &&
    Math.abs(Number(place.y) - Number(result.y)) < 0.000001;
  const sameText =
    place.name.trim() === result.name.trim() &&
    (place.address ?? '').trim() === (result.roadAddress || result.address || '').trim();
  return Boolean(sameUrl || sameCoords || sameText);
}

function kakaoDirectionUrl(name: string, x?: string | null, y?: string | null) {
  if (!x || !y) return null;
  return `https://map.kakao.com/link/to/${encodeURIComponent(name)},${y},${x}`;
}
function coordsFromPlace(place: PlaceCandidate | null | undefined): RouteCoords | null {
  if (!place?.x || !place.y) return null;
  return { x: place.x, y: place.y };
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}
function toTimeLocal(value?: string | null) {
  return toDateTimeLocal(value).slice(11, 16);
}

function mergeDateAndTime(localDateTime: string, time: string) {
  if (!localDateTime || !time) return localDateTime;
  return `${localDateTime.slice(0, 10)}T${time}`;
}

type CourseDraft = {
  title: string;
  startAt: string;
  memo: string;
};

function draftFromCourse(item: ScheduleItem): CourseDraft {
  return {
    title: item.title,
    startAt: toDateTimeLocal(item.startAt),
    memo: item.memo ?? '',
  };
}


export default function PlacePage() {
  const { id: meetingId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forParam = searchParams.get('for');
  const isMeetingPointMode = forParam === 'meeting-point';
  const isMapMode = searchParams.get('mode') === 'map';
  const scheduleTargetId =
    searchParams.get('schedule') ?? (forParam && !isMeetingPointMode ? forParam : null);
  const alsoSetMeetingPoint = searchParams.get('asMeetingPoint') === '1';
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [showMap, setShowMap] = useState(isMeetingPointMode || isMapMode || Boolean(scheduleTargetId));
  const mapCloseRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (showMap) mapCloseRef.current?.focus();
  }, [showMap]);
  const [manageCourse, setManageCourse] = useState(false);
  const [addingCourse, setAddingCourse] = useState(false);
  const [courseDrafts, setCourseDrafts] = useState<Record<string, CourseDraft>>({});
  const [deletingCourseId, setDeletingCourseId] = useState<string | null>(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [courseStartAt, setCourseStartAt] = useState('');
  const [courseMemo, setCourseMemo] = useState('');
  const meetingQ = useQuery({
    queryKey: ['meeting', meetingId],
    queryFn: () => getMeeting(meetingId),
    enabled: !!meetingId,
  });
  const placesQ = useQuery({
    queryKey: ['places', meetingId],
    queryFn: () => listPlaceCandidates(meetingId),
    enabled: !!meetingId,
  });
  const scheduleQ = useQuery({
    queryKey: ['schedule', meetingId],
    queryFn: () => listScheduleItems(meetingId),
    enabled: !!meetingId,
  });

  const setPointMut = useMutation({
    mutationFn: (place: PlaceResult) =>
      updateMeetingPoint(meetingId, {
        name: place.name,
        x: String(place.x),
        y: String(place.y),
      }),
    onSuccess: (m) => {
      qc.setQueryData(['meeting', meetingId], m);
      toast.success(`만나는 장소를 '${m.meetingPointName}'(으)로 정했어요.`);
      navigate(`/meetings/${meetingId}/vote`);
    },
    onError: () => toast.error('만나는 장소 설정에 실패했어요.'),
  });

  const addPlaceMut = useMutation({
    mutationFn: async (place: PlaceResult) => {
      const candidate = await createPlaceCandidate(meetingId, {
        name: place.name,
        category: guessCategory(place.category, place.name),
        url: place.placeUrl,
        address: place.roadAddress || place.address,
        x: String(place.x),
        y: String(place.y),
        ...(scheduleTargetId ? { scheduleItemId: scheduleTargetId } : {}),
      });
      if (alsoSetMeetingPoint) {
        const meeting = await updateMeetingPoint(meetingId, {
          name: place.name,
          x: String(place.x),
          y: String(place.y),
        });
        qc.setQueryData(['meeting', meetingId], meeting);
      }
      return candidate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['places', meetingId] });
      if (alsoSetMeetingPoint) qc.invalidateQueries({ queryKey: ['meeting', meetingId] });
      toast.show(alsoSetMeetingPoint ? '첫 장소와 집결지를 설정했어요.' : '장소 후보로 추가했어요.');
      navigate(
        scheduleTargetId
          ? `/meetings/${meetingId}/vote?tab=place&schedule=${scheduleTargetId}`
          : `/meetings/${meetingId}/vote`,
      );
    },
    onError: () => {
      toast.show('장소 추가에 실패했어요. 다시 시도해주세요.');
    },
  });

  const addCourseMut = useMutation({
    mutationFn: () =>
      createScheduleItem(meetingId, {
        title: courseTitle.trim(),
        startAt: new Date(courseStartAt).toISOString(),
        memo: courseMemo.trim(),
      }),
    onSuccess: () => {
      setAddingCourse(false);
      setCourseTitle('');
      setCourseStartAt('');
      setCourseMemo('');
      qc.invalidateQueries({ queryKey: ['schedule', meetingId] });
      toast.success('코스를 추가했어요.');
    },
    onError: () => toast.error('코스 추가에 실패했어요.'),
  });

  const updateCourseMut = useMutation({
    mutationFn: async (
      updates: Array<{ id: string; draft: CourseDraft }>,
    ) => {
      for (const { id, draft } of updates) {
        await updateScheduleItem(id, {
          title: draft.title.trim(),
          startAt: new Date(draft.startAt).toISOString(),
          memo: draft.memo.trim(),
        });
      }
      return updates.length;
    },
    onSuccess: (count) => {
      setManageCourse(false);
      setCourseDrafts({});
      qc.invalidateQueries({ queryKey: ['schedule', meetingId] });
      toast.success(count > 0 ? '코스를 수정했어요.' : '수정할 내용이 없어요.');
    },
    onError: () => toast.error('코스 수정에 실패했어요.'),
  });

  const deleteCourseMut = useMutation({
    mutationFn: (scheduleItemId: string) => deleteScheduleItem(scheduleItemId),
    onSuccess: () => {
      setDeletingCourseId(null);
      setCourseDrafts((prev) => {
        const next = { ...prev };
        delete next[deletingCourseId ?? ''];
        return next;
      });
      qc.invalidateQueries({ queryKey: ['schedule', meetingId] });
      toast.success('코스를 삭제했어요.');
    },
    onError: () => toast.error('코스 삭제에 실패했어요.'),
  });

  function openAddCourse() {
    const last = scheduleItems[scheduleItems.length - 1];
    const seed = last?.startAt ?? meetingQ.data?.confirmedDate ?? new Date().toISOString();
    const next = new Date(seed);
    if (last) next.setHours(next.getHours() + 1);
    setCourseTitle('');
    setCourseStartAt(toDateTimeLocal(next.toISOString()));
    setCourseMemo('');
    setAddingCourse(true);
  }


  const candidates = placesQ.data ?? [];
  const visibleMapCandidates = scheduleTargetId
    ? candidates.filter((place) => place.scheduleItemId === scheduleTargetId)
    : candidates;
  const selectedCandidate = selectedPlace
    ? candidates.find((place) =>
        sameCandidate(place, selectedPlace) &&
        (!scheduleTargetId || place.scheduleItemId === scheduleTargetId)
      )
    : null;
  const meetingPoint = meetingQ.data?.meetingPointName
    ? {
        name: meetingQ.data.meetingPointName,
        x: meetingQ.data.meetingPointX,
        y: meetingQ.data.meetingPointY,
      }
    : null;
  const meetingPointReference =
    meetingPoint?.x && meetingPoint.y
      ? {
          name: meetingPoint.name,
          x: Number(meetingPoint.x),
          y: Number(meetingPoint.y),
        }
      : null;
  const routeOrigin =
    meetingPoint?.x && meetingPoint.y
      ? { x: meetingPoint.x, y: meetingPoint.y }
      : null;
  const mapSeeds = visibleMapCandidates
    .filter((p) => p.x && p.y)
    .map(candidateToPlaceResult);
  const scheduleItems = scheduleQ.data ?? [];
  useEffect(() => {
    if (!manageCourse) return;
    setCourseDrafts((prev) => {
      const next: Record<string, CourseDraft> = {};
      scheduleItems.forEach((item) => {
        next[item.id] = prev[item.id] ?? draftFromCourse(item);
      });
      return next;
    });
  }, [manageCourse, scheduleItems]);

  function changeCourseDraft(id: string, patch: Partial<CourseDraft>) {
    setCourseDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { title: '', startAt: '', memo: '' }), ...patch },
    }));
  }

  function startCourseManage() {
    setCourseDrafts(Object.fromEntries(scheduleItems.map((item) => [item.id, draftFromCourse(item)])));
    setManageCourse(true);
  }

  function finishCourseManage() {
    const drafts = scheduleItems.map((item) => ({
      item,
      draft: courseDrafts[item.id] ?? draftFromCourse(item),
    }));

    if (drafts.some(({ draft }) => !draft.title.trim() || !draft.startAt || Number.isNaN(new Date(draft.startAt).getTime()))) {
      toast.error('이름과 시간을 확인해주세요.');
      return;
    }

    const updates = drafts.filter(({ item, draft }) =>
      draft.title.trim() !== item.title ||
      new Date(draft.startAt).toISOString() !== new Date(item.startAt).toISOString() ||
      draft.memo.trim() !== (item.memo ?? ''),
    );

    updateCourseMut.mutate(updates.map(({ item, draft }) => ({ id: item.id, draft })));
  }
  const placesById = new Map(candidates.map((place) => [place.id, place]));

  const directionsUrl =
    kakaoDirectionUrl(
      meetingPoint?.name ?? '',
      meetingPoint?.x,
      meetingPoint?.y,
    );
  const dest = selectedPlace
    ? { x: String(selectedPlace.x), y: String(selectedPlace.y) }
    : null;

  return (
    <section className={selectedPlace ? 'space-y-4 pb-72' : 'space-y-4 pb-24'}>
      <h1 className="sr-only">코스</h1>
      <MeetingTabs meetingId={meetingId} />

      <section aria-labelledby="meeting-point-heading" className="space-y-2">
        <h2 id="meeting-point-heading" className="text-feature font-semibold tracking-tight-feature text-ink">
          집결지
        </h2>
      <Card className="!overflow-hidden !border-surface-border !p-0">
        <div className="relative h-[76px] bg-gradient-to-br from-primary-soft to-success-soft">
          <div className="absolute left-[-24px] top-8 h-3.5 w-40 rotate-[-18deg] rounded-full bg-primary-light/35" />
          <div className="absolute right-[-28px] top-14 h-3.5 w-36 rotate-[22deg] rounded-full bg-primary-light/35" />
          <div className="absolute left-1/2 top-8 h-9 w-9 -translate-x-1/2 rotate-[-45deg] rounded-[50%_50%_50%_4px] bg-primary shadow-card">
            <div className="absolute inset-2.5 rounded-full bg-surface" />
          </div>
        </div>
        <div className="p-3">
          <div className="flex items-start justify-between gap-3">
            <Link
              to={`/meetings/${meetingId}/point`}
              className="min-w-0 rounded-xl focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              <p className="text-[12px] font-medium text-ink-faint">
                {meetingPoint ? '현재 집결지' : '미정'}
              </p>
              <p className="mt-1 truncate text-[17px] font-semibold leading-tight tracking-tight text-ink">
                {meetingPoint?.name ?? '집결지 설정 필요'}
              </p>
              <p className="mt-1 truncate text-small text-ink-muted">
                {meetingPoint ? '장소 검색의 출발 기준' : '지도에서 집결지를 정해주세요'}
              </p>
            </Link>
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                meetingPoint
                  ? 'bg-success-soft text-success-strong'
                  : 'bg-warning-soft text-warning-strong'
              }`}
            >
              {meetingPoint ? '확정' : '대기'}
            </span>
          </div>
          {meetingPoint ? (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                variant="brand"
                className="gap-1.5"
                onClick={() => {
                  const target = directionsUrl;
                  if (target) window.open(target, '_blank', 'noopener,noreferrer');
                }}
                disabled={!directionsUrl}
              >
                <Icon name="map" size={16} />
                길찾기
              </Button>
              <Button
                variant="secondary"
                className="gap-1.5"
                onClick={() => navigate(`/meetings/${meetingId}/vote`)}
              >
                <Icon name="vote" size={16} />
                투표
              </Button>
            </div>
          ) : (
            <Button
              variant="brand"
              className="mt-3 w-full gap-1.5"
              onClick={() => navigate(`/meetings/${meetingId}/places?for=meeting-point`)}
            >
              <Icon name="pin" size={16} />
              집결지 선택
            </Button>
          )}
        </div>
      </Card>
      </section>


      <section aria-labelledby="course-slots" className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 id="course-slots" className="text-feature font-semibold tracking-tight-feature text-ink">
            시간대별 코스
          </h2>
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-primary-soft px-2 py-1 text-[11px] font-semibold text-primary">
              {scheduleItems.length}칸
            </span>
            <button
              type="button"
              onClick={() => {
                if (manageCourse) finishCourseManage();
                else startCourseManage();
              }}
              disabled={updateCourseMut.isPending}
              className="pressable rounded-lg bg-surface-muted px-2.5 py-1.5 text-[11px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-50"
            >
              {manageCourse ? (updateCourseMut.isPending ? '저장 중' : '수정 완료') : '수정'}
            </button>
          </div>
        </div>
        {manageCourse ? (
          <button
            type="button"
            onClick={openAddCourse}
            className="pressable flex min-h-11 w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-surface-border bg-surface px-3 text-small font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            <Icon name="plus" size={15} />
            코스 추가
          </button>
        ) : null}
        {scheduleQ.isLoading ? (
          <Card className="!border-surface-border !p-4">
            <p className="text-center text-body text-ink-muted">불러오는 중…</p>
          </Card>
        ) : scheduleItems.length > 0 ? (
          manageCourse ? (
            <ol className="space-y-2" aria-label="시간대별 코스 수정">
              {scheduleItems.map((item) => {
                const place = item.placeCandidateId ? placesById.get(item.placeCandidateId) : null;
                const draft = courseDrafts[item.id] ?? draftFromCourse(item);
                return (
                  <li
                    key={item.id}
                    className="rounded-[20px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3 py-3"
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={toTimeLocal(draft.startAt)}
                            onChange={(event) =>
                              changeCourseDraft(item.id, {
                                startAt: mergeDateAndTime(draft.startAt, event.target.value),
                              })
                            }
                            className="h-10 w-[96px] rounded-xl bg-surface-muted px-2 text-center text-small font-semibold text-ink outline-none focus:shadow-focus-ring"
                            aria-label={`${item.title} 시간`}
                          />
                          <input
                            value={draft.title}
                            onChange={(event) => changeCourseDraft(item.id, { title: event.target.value })}
                            className="h-10 min-w-0 flex-1 rounded-xl bg-surface-muted px-3 text-ui font-semibold text-ink outline-none focus:shadow-focus-ring"
                            aria-label={`${item.title} 이름`}
                          />
                        </div>
                        <input
                          value={draft.memo}
                          onChange={(event) => changeCourseDraft(item.id, { memo: event.target.value })}
                          placeholder={place?.name ?? '메모'}
                          className="h-10 w-full rounded-xl bg-surface-subtle px-3 text-small text-ink-muted outline-none focus:shadow-focus-ring"
                          aria-label={`${item.title} 메모`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDeletingCourseId(item.id)}
                        className="pressable inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-error-soft text-error focus-visible:outline-none focus-visible:shadow-focus-ring"
                        aria-label={`${item.title} 삭제`}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <ol className="space-y-2" aria-label="시간대별 코스">
              {scheduleItems.map((item, index) => {
                const place = item.placeCandidateId ? placesById.get(item.placeCandidateId) : null;
                const nextItem = scheduleItems[index + 1];
                const nextPlace = nextItem?.placeCandidateId
                  ? placesById.get(nextItem.placeCandidateId)
                  : null;
                const currentCoords = coordsFromPlace(place);
                const nextCoords = coordsFromPlace(nextPlace);
                const transition =
                  currentCoords && nextCoords ? (
                    <RouteBadge origin={currentCoords} dest={nextCoords} initialMode="walk" compact />
                  ) : null;
                const isLast = index === scheduleItems.length - 1;
                return (
                  <li key={item.id} className="grid grid-cols-[44px_16px_1fr] gap-2">
                    <span className="pt-2 text-right text-[11px] font-medium text-ink-faint">
                      {fmtKst(item.startAt, 'HH:mm')}
                    </span>
                    <span className="relative flex flex-col items-center pt-2">
                      {!isLast ? (
                        <span
                          className="absolute bottom-[-22px] left-1/2 top-[14px] w-0.5 -translate-x-1/2 rounded-full bg-gradient-to-b from-primary/80 to-primary/10"
                          aria-hidden={transition ? undefined : true}
                        >
                          {transition ? (
                            <span
                              data-testid="course-transition"
                              className="pointer-events-none absolute left-1/2 top-1/2 z-10 block w-max -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-surface px-1 text-[10px] font-medium leading-[12px] tracking-[-0.35px] text-ink-faint"
                            >
                              {transition}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                      <span className="relative z-20 h-3 w-3 rounded-full bg-primary shadow-[0_0_0_5px_rgba(120,201,87,0.14)]" />
                    </span>
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/meetings/${meetingId}/schedule/${item.id}`)}
                        className="w-full rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-[16px] font-semibold leading-5 text-ink">{item.title}</p>
                            <p className="mt-0.5 truncate text-small text-ink-muted">
                              {place?.name ?? item.memo ?? '장소 투표 필요'}
                            </p>
                          </div>
                          <span className="shrink-0 rounded-full bg-primary-soft px-2 py-1 text-[12px] font-semibold text-primary">
                            {place ? '확정' : '투표'}
                          </span>
                        </div>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )
        ) : (
          <Card className="!border-surface-border !p-4">
            <p className="text-center text-body text-ink-muted">코스 슬롯을 추가하세요.</p>
          </Card>
        )}
      </section>


      {showMap ? (
        <div
          className="fixed inset-0 z-50 bg-canvas"
          role="dialog"
          aria-modal="true"
          aria-labelledby="place-map-flow"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setShowMap(false);
              setSelectedPlace(null);
            }
          }}
        >
          <div className="mx-auto flex h-[100dvh] w-full max-w-screen-sm flex-col overflow-hidden bg-canvas">
            <header className="shrink-0 border-b border-surface-border/70 bg-canvas/92 px-3 py-2 pt-[max(env(safe-area-inset-top),8px)] shadow-sm backdrop-blur-xl">
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  ref={mapCloseRef}
                  onClick={() => {
                    setShowMap(false);
                    setSelectedPlace(null);
                  }}
                  className="pressable inline-flex h-9 items-center justify-center gap-1 rounded-full bg-surface-muted px-3 text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
                  aria-label="지도 검색 닫기"
                >
                  <Icon name="back" size={18} />
                  <span className="text-[12px] font-semibold">닫기</span>
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-ink-faint">지도 검색</p>
                  <h2 id="place-map-flow" className="truncate text-[15px] font-semibold leading-tight text-ink">
                    {meetingPointReference ? meetingPointReference.name : '현재 지도 기준'}
                  </h2>
                </div>
              </div>
            </header>
            <section aria-labelledby="place-map-flow" className="min-h-0 flex-1">
              <PlaceMapView
                places={mapSeeds}
                referencePoint={meetingPointReference}
                variant="fullscreen"
                selectedPlaceId={selectedPlace?.id ?? null}
                onSelect={setSelectedPlace}
              />
            </section>
          </div>
        </div>
      ) : null}

      {selectedPlace && (
        <div className={`fixed inset-x-0 mx-auto w-full max-w-screen-sm px-4 pb-[max(env(safe-area-inset-bottom),8px)] ${showMap ? 'bottom-0 z-[70]' : 'bottom-[62px] z-30'}`}>
          <Card className="!border-surface-border/80 shadow-sheet">
            <div className="space-y-1.5">
              <p className="text-ui font-semibold text-ink">{selectedPlace.name}</p>
              <p className="truncate text-small text-ink-muted">
                {selectedPlace.roadAddress || selectedPlace.address}
              </p>
              {dest && routeOrigin && (
                <div className="space-y-1">
                  <RouteBadge origin={routeOrigin} dest={dest} initialMode="walk" />
                  <p className="text-tag text-ink-muted">집결지 기준</p>
                </div>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              {isMeetingPointMode ? (
                <Button
                  variant="brand"
                  className="flex-1"
                  disabled={setPointMut.isPending}
                  onClick={() => setPointMut.mutate(selectedPlace)}
                >
                  {setPointMut.isPending ? '설정 중…' : '집결지 설정'}
                </Button>
              ) : (
                <Button
                  variant="brand"
                  className="flex-1"
                  disabled={addPlaceMut.isPending || Boolean(selectedCandidate)}
                  onClick={() => addPlaceMut.mutate(selectedPlace)}
                >
                  {selectedCandidate ? '이미 후보' : addPlaceMut.isPending ? '추가 중…' : '후보 추가'}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setSelectedPlace(null)}>
                취소
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Dialog
        open={addingCourse}
        title="코스 추가"
        confirmLabel={addCourseMut.isPending ? '추가 중…' : '추가'}
        confirmDisabled={!courseTitle.trim() || !courseStartAt || addCourseMut.isPending}
        onConfirm={() => addCourseMut.mutate()}
        onCancel={() => setAddingCourse(false)}
      >
        <div className="space-y-3">
          <label className="block text-small font-semibold text-ink">
            할 일
            <input
              value={courseTitle}
              onChange={(event) => setCourseTitle(event.target.value)}
              className="mt-1 w-full rounded-xl border border-surface-border bg-surface px-3 py-2 text-body text-ink outline-none focus:shadow-focus-ring"
              placeholder="예: 점심, 카페, 방탈출"
            />
          </label>
          <label className="block text-small font-semibold text-ink">
            시간
            <input
              type="datetime-local"
              value={courseStartAt}
              onChange={(event) => setCourseStartAt(event.target.value)}
              className="mt-1 w-full rounded-xl border border-surface-border bg-surface px-3 py-2 text-body text-ink outline-none focus:shadow-focus-ring"
            />
          </label>
          <label className="block text-small font-semibold text-ink">
            메모
            <input
              value={courseMemo}
              onChange={(event) => setCourseMemo(event.target.value)}
              className="mt-1 w-full rounded-xl border border-surface-border bg-surface px-3 py-2 text-body text-ink outline-none focus:shadow-focus-ring"
              placeholder="선택"
            />
          </label>
        </div>
      </Dialog>


      <Dialog
        open={deletingCourseId !== null}
        title="코스를 삭제할까요?"
        description="연결된 비용과 댓글도 함께 정리돼요."
        confirmLabel={deleteCourseMut.isPending ? '삭제 중…' : '삭제'}
        confirmTone="danger"
        confirmDisabled={deleteCourseMut.isPending}
        onConfirm={() => deletingCourseId && deleteCourseMut.mutate(deletingCourseId)}
        onCancel={() => setDeletingCourseId(null)}
      />
    </section>
  );
}
